import App from "./App.tsx";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router";
import ReceiptPublicPage from "./pages/receipt/Page.tsx";
import "./assets/styles/index.css";
import dayjs from "dayjs";
import "dayjs/locale/uz-latn.js";
import "react-lazy-load-image-component/src/effects/blur.css";

import i18n from "./lib/i18n";

import "./mockEnv.ts";
import { initSDK } from "./init.ts";
import { QueryProvider } from "@/providers/QueryProvider.tsx";
import DrawerCSSProvider from "@/providers/DrawerCSSProvider.tsx";
import { Toaster } from "@/components/ui/sonner.tsx";
import Explosion from "./components/common/explosion/Explosion.tsx";

import * as Sentry from "@sentry/browser";

const PII_PARAM_RE = /(phone|phone_number|init_data|initData|telegram_id)=[^&]*/gi;

Sentry.init({
  dsn: "https://764f4134110b89fb793a664f0b0c476e@o4511569270538240.ingest.de.sentry.io/4511573779087440",
  sendDefaultPii: false,
  beforeSend(event) {
    try {
      const req = event.request;
      if (req?.headers) {
        const headers = req.headers as Record<string, string>;
        for (const k of Object.keys(headers)) {
          const lower = k.toLowerCase();
          if (
            lower === "authorization" ||
            lower === "cookie" ||
            lower === "set-cookie"
          ) {
            headers[k] = "[Filtered]";
          }
        }
      }
      if (req?.query_string && typeof req.query_string === "string") {
        req.query_string = req.query_string.replace(
          PII_PARAM_RE,
          "$1=[Filtered]",
        );
      }
      if (event.user) {
        const u = event.user as Record<string, unknown>;
        for (const k of ["phone", "phone_number", "email", "ip_address"]) {
          if (k in u) u[k] = "[Filtered]";
        }
      }
    } catch {
      return null;
    }
    return event;
  },
});

dayjs.locale(i18n.language === "uz" ? "uz-latn" : i18n.language);

const root = createRoot(document.getElementById("root")!);

function renderRoot() {
  root.render(
    <BrowserRouter basename="/">
      <QueryProvider>
        <DrawerCSSProvider>
          <App />
          <Toaster position="top-center" />
          <Explosion />
        </DrawerCSSProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

// Opened outside Telegram (SDK init failed) → bounce to the Telegram
// mini-app. The app only runs inside the Telegram SDK; there is no web
// landing page.
function redirectToTelegram() {
  window.location.href = import.meta.env.VITE_APP_TG_URL;
}

// Public receipt (chek) page — opened from a shared link in any browser.
// Render it standalone, BEFORE the Telegram SDK init (whose failure path
// redirects to Telegram), so a recipient without Telegram can still view and
// download the chek.
function renderReceiptPublic() {
  root.render(
    <BrowserRouter basename="/">
      <Routes>
        <Route path="/receipt/:id" element={<ReceiptPublicPage />} />
      </Routes>
    </BrowserRouter>
  );
}

// Configure all application dependencies

if (window.location.pathname.startsWith("/receipt/")) {
  renderReceiptPublic();
} else {
  initSDK()
    .then(() => {
      renderRoot();
    })
    .catch((e) => {
      console.error(e);
      // Vite's development mock can briefly fail SDK setup during a hard
      // reload. Keep the local app available instead of navigating away from
      // the dev server. Production still redirects unsupported browsers to
      // the Telegram mini-app as before.
      if (import.meta.env.DEV) {
        renderRoot();
        return;
      }
      redirectToTelegram();
      return;
    });
}
